import { DEFAULT_OPTIONS, InnerCarousel } from 'components/Carousel/Carousel'
import { ASPECT_RATIOS, AspectRatioKey } from 'components/NewFileModal/Types'
import { IFilters } from 'components/Templates/TemplatesModalV2/Types'
import useEmblaCarousel from 'embla-carousel-react'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { useVisualTemplates } from 'hooks/useVisualTemplates'
import { useRef } from 'react'
import { collectResults } from 'utils/query'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { startCase } from 'lodash'
import Styles from './TemplatesV2.module.scss'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { PreviousButton } from 'screens/blaze/Home/components/Catalogue/components/ScrollButton/PreviousButton'
import { NextButton } from 'screens/blaze/Home/components/Catalogue/components/ScrollButton/NextButton'
import { TemplatePreview } from './TemplatePreview'
import cx from 'classnames'
import { getTemplateDisplayName } from 'components/Templates/TemplatesModalV2/components/TemplatesGrid'

const WIDE_ASPECT_RATIO = 1.5
const NARROW_ASPECT_RATIO = 0.7
const WIDE_ITEM_WIDTH = 320
const NARROW_ITEM_WIDTH = 184
const MEDIUM_ITEM_WIDTH = 235
const SMALL_WIDE_ITEM_WIDTH = 245
const SMALL_NARROW_ITEM_WIDTH = 140
const SMALL_MEDIUM_ITEM_WIDTH = 180

interface Props {
  style: string
  filters: Omit<IFilters, 'aspectRatioKey'>
  aspectRatioKey: AspectRatioKey
  onSelect: (templateId: number, aspectRatioKey: AspectRatioKey) => void
  selectedIds?: number[]
  hideFormatLabels?: boolean
  smallPreviews?: boolean
}

export const getItemWidth = (width: number, height: number, smallPreviews: boolean) => {
  if (width / height > WIDE_ASPECT_RATIO) {
    return smallPreviews ? SMALL_WIDE_ITEM_WIDTH : WIDE_ITEM_WIDTH
  } else if (width / height < NARROW_ASPECT_RATIO) {
    return smallPreviews ? SMALL_NARROW_ITEM_WIDTH : NARROW_ITEM_WIDTH
  }

  return smallPreviews ? SMALL_MEDIUM_ITEM_WIDTH : MEDIUM_ITEM_WIDTH
}

export const StyleCarousel = ({
  style,
  filters,
  aspectRatioKey,
  onSelect,
  selectedIds = [],
  hideFormatLabels = false,
  smallPreviews = false,
}: Props) => {
  const { categories, types, query, styles: filterStyles } = filters
  const { width, height, name: aspectRatioName } = ASPECT_RATIOS[aspectRatioKey]

  const isWide = width / height > WIDE_ASPECT_RATIO
  const isNarrow = width / height < NARROW_ASPECT_RATIO

  const itemWidth = getItemWidth(width, height, smallPreviews)

  const ITEMS_TO_LOAD = 10
  const elementRef = useRef<HTMLDivElement>(null)
  const intersection = useIntersectionObserver(elementRef, { threshold: [0, 0.00000001] })
  const isVisible = (intersection?.intersectionRatio ?? 0) >= 0.00000001

  const visualTemplatesQuery = useVisualTemplates(
    {
      width,
      height,
      element_type: 'Template',
      items: ITEMS_TO_LOAD,
      category: categories === 'all' ? undefined : ([...categories] as string[]),
      style: [style, ...(filterStyles === 'all' ? [] : ([...filterStyles] as string[]))],
      type: types === 'all' ? undefined : ([...types] as string[]),
      query: query === '' ? undefined : query,
      order_by: 'random',
    },
    {
      enabled: isVisible,
      cacheTime: 2 * 1000 * 60,
      staleTime: Infinity,
    },
  )

  const templates = collectResults(visualTemplatesQuery, (data) => data.visual_elements)

  const embla = useEmblaCarousel({
    ...DEFAULT_OPTIONS,
    dragFree: true,
    align: 'end',
    slidesToScroll: templates.length > 1 ? 'auto' : 1,
  })
  const emblaApi = embla[1]
  const { fetchNextPage, hasNextPage, isLoading } = visualTemplatesQuery

  if (!isLoading && templates.length === 0) {
    return <div ref={elementRef} data-test-id={style} />
  }

  return (
    <div ref={elementRef} className={Styles.styleCarouselRoot} data-test-id={style}>
      <div className={Styles.heading}>
        <Heading level={3}>{startCase(style)}</Heading>
        {emblaApi && templates.length > 1 && (
          <div className={Styles.controls}>
            <PreviousButton emblaApi={emblaApi} />
            <NextButton emblaApi={emblaApi} />
          </div>
        )}
      </div>
      <InnerCarousel embla={embla} withControls={false}>
        {isLoading &&
          Array.from({ length: 10 }).map((_, i) => (
            <div
              className={cx(Styles.templatePreviewRoot, Styles.loadingContainer, {
                [Styles.small]: smallPreviews,
                [Styles.isWide]: isWide,
                [Styles.isNarrow]: isNarrow,
              })}
              key={i}
            >
              <ShimmeringColor aspectRatio={width / height} width={itemWidth} key={i} />
            </div>
          ))}
        {!isLoading &&
          templates.map((template, i) => (
            <div
              className={cx(Styles.templatePreviewRoot, {
                [Styles.small]: smallPreviews,
                [Styles.isWide]: isWide,
                [Styles.isNarrow]: isNarrow,
              })}
              key={i}
            >
              <TemplatePreview
                template={template}
                onPress={() => onSelect(template.id, aspectRatioKey)}
                itemWidth={itemWidth}
                aspectRatio={width / height}
                isSelected={selectedIds.includes(template.id)}
              />
              <div className={Styles.templateLabels}>
                <Text variant="smallList" lineClamp={1}>
                  {getTemplateDisplayName(template.name)}
                </Text>
                {!hideFormatLabels && <Text variant="metadata">{aspectRatioName} by Blaze.ai</Text>}
              </div>
            </div>
          ))}
        {hasNextPage && (
          <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage}>
            <ShimmeringColor aspectRatio={width / height} width={itemWidth} />
          </InfiniteScroll>
        )}
        {hasNextPage &&
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i}>
              <ShimmeringColor aspectRatio={width / height} width={itemWidth} />
            </div>
          ))}
      </InnerCarousel>
    </div>
  )
}
